import React, { useCallback, useEffect, useState } from "react";
import { Modal, Button, Accordion, Spinner, Alert } from "react-bootstrap";
import Swal from "sweetalert2";
import { getActasPorOt } from "../../api/common";
import { PdfActa } from "../Pdfs/PdfActa";
import ActaAccordionItem from "./ActaAccordionItem";

/**
 * Modal que muestra las actas asociadas a una OT desde el dropdown de acciones del
 * listado de Órdenes de Trabajo (TIN-15).
 *
 * Props:
 * - show: bool — controla la visibilidad del modal.
 * - onHide: function — callback cuando el usuario cierra.
 * - otId: string — _id de la OT seleccionada.
 * - otConsecutivo: string|number — número de orden para el título.
 */
export const ActasOTModal = ({ show, onHide, otId, otConsecutivo }) => {
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState(null);
  const [actas, setActas] = useState([]);

  const cargarActas = useCallback(async () => {
    if (!otId) return;
    try {
      setCargando(true);
      setError(null);
      const respuesta = await getActasPorOt(otId);
      // Backend responde con { data: { data: [...], total, page, limit } }
      const listado =
        respuesta?.data?.data?.data ??
        respuesta?.data?.data ??
        respuesta?.data ??
        [];
      setActas(Array.isArray(listado) ? listado : []);
    } catch (err) {
      console.error("[ActasOTModal] Error al cargar actas:", err);
      setError("No se pudo cargar el listado de actas. Intente de nuevo.");
    } finally {
      setCargando(false);
    }
  }, [otId]);

  useEffect(() => {
    if (show && otId) {
      cargarActas();
    }
    if (!show) {
      // Reset al cerrar — evita mostrar datos obsoletos al reabrir con otra OT
      setActas([]);
      setError(null);
      setCargando(false);
    }
  }, [show, otId, cargarActas]);

  const handleDescargarPdf = (acta) => {
    try {
      // PdfActa (cliente-side) espera: numeroOrden, Equipos, autor, orden.cliente, etc.
      // El DTO del backend ya incluye `Equipos`, `autor`, `orden.cliente` populados;
      // sólo se asegura `numeroOrden` desde el otConsecutivo del modal por compatibilidad.
      const infoActa = {
        ...acta,
        numeroOrden: acta?.numeroOrden || acta?.orden?.numeroOrden || otConsecutivo,
      };
      PdfActa(infoActa);
    } catch (err) {
      console.error("[ActasOTModal] Error al generar PDF:", err);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "No se pudo descargar el PDF del acta.",
        toast: true,
        position: "top-end",
        showConfirmButton: false,
        timer: 3000,
      });
    }
  };

  const handleCerrar = () => {
    setActas([]);
    setError(null);
    setCargando(false);
    if (onHide) onHide();
  };

  const renderCuerpo = () => {
    if (cargando) {
      return (
        <div className="d-flex justify-content-center align-items-center py-5">
          <Spinner animation="border" variant="primary" />
          <span className="ms-3 text-muted">Cargando actas...</span>
        </div>
      );
    }

    if (error) {
      return (
        <Alert variant="danger" className="mb-0">
          <Alert.Heading>Error</Alert.Heading>
          <p className="mb-3">{error}</p>
          <Button variant="outline-danger" size="sm" onClick={cargarActas}>
            <i className="bi bi-arrow-clockwise me-2" />
            Reintentar
          </Button>
        </Alert>
      );
    }

    if (actas.length === 0) {
      return (
        <p className="text-center text-muted fst-italic py-4 mb-0">
          No hay actas creadas para esta orden de trabajo.
        </p>
      );
    }

    return (
      <Accordion alwaysOpen>
        {actas.map((acta) => (
          <ActaAccordionItem
            key={acta._id}
            eventKey={acta._id}
            acta={acta}
            onDescargarPdf={handleDescargarPdf}
          />
        ))}
      </Accordion>
    );
  };

  return (
    <Modal show={show} onHide={handleCerrar} size="xl" scrollable centered>
      <Modal.Header closeButton>
        <Modal.Title>
          <i className="bi bi-journal-text me-2" />
          Actas de la OT #{otConsecutivo || ""}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>{renderCuerpo()}</Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleCerrar}>
          Cerrar
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ActasOTModal;
